/**
 * Composite Analyzer: Mistake Clustering (setup scope)
 *
 * Mistakes (`trade.mistakes`) are tagged manually by the trader as a kind of
 * post-mortem confession — "I FOMO'd this," "I broke risk," "I revenge-traded."
 * Existing analyzers (mistakeTaxonomy at session scope, dayMistakes at day
 * scope) do flat-list summaries: "your top mistake is X."
 *
 * What no analyzer currently does: the CROSS-CUT. Where a specific mistake
 * clusters relative to the rest of the trader's history, when in the trading
 * day it spikes, and whether it's the cause of this setup's losses or just
 * incidental noise. Three patterns:
 *
 *   1. Setup-specific mistake spike — this setup's mistake-per-trade ratio
 *      is meaningfully higher than the trader's overall average. The setup
 *      is structurally inviting a particular kind of error.
 *
 *   2. Time-of-day mistake clustering — within this setup's history,
 *      mistakes cluster in specific hour buckets (e.g., 9:30-10:30 or
 *      after 14:00). Surfacing the window lets the trader recognize the
 *      setup-with-fatigue or setup-with-open-volatility pairing.
 *
 *   3. Loss-cause attribution — for losers on this setup that have at least
 *      one mistake tag, which mistake recurs most often? The "this setup
 *      loses because of X" answer.
 *
 * Reads:
 *   trade.mistakes        — array of mistake IDs or names (user-tagged)
 *   trade.netPnl          — for win/loss bucketing (must be enriched by scope)
 *   trade.entryTime       — HH:MM in ET, for hour bucketing
 *   trade.setup           — for cross-setup baseline computation
 *   ctx.allMistakes       — mistake library, for label resolution
 *   ctx.allTrades         — for cross-setup baseline (rate comparison)
 *
 * Trades without a mistake field are silently treated as zero-mistake. No
 * backfill prerequisite — this analyzer runs on data the trader is
 * already capturing manually.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';

export const key = 'mistakeClustering';
export const tier = 'free';

const SETUP_RATE_MIN_TRADES   = 6;     // Need this many setup trades for a meaningful rate
const BASELINE_MIN_TRADES     = 12;    // Need this many cross-setup trades for a meaningful baseline
const SETUP_RATE_GAP          = 0.20;  // 20pp lift over baseline = "spike"
const HOUR_BUCKET_MIN_TRADES  = 4;     // Don't surface an hour bucket unless 4+ trades fell in it
const HOUR_BUCKET_MIN_LIFT    = 1.5;   // Bucket's mistake-rate must be ≥1.5× setup average
const LOSS_CAUSE_MIN_LOSERS   = 4;
const LOSS_CAUSE_MIN_SHARE    = 0.50;  // Top mistake must appear in ≥50% of mistake-tagged losers

const fmtPct = (n) => `${Math.round(n * 100)}%`;

/**
 * Build an id-or-name → label resolver. trade.mistakes can hold either the
 * library id OR a user-typed string; both should resolve to a human label.
 */
const buildLabelMap = (allMistakes) => {
  const m = new Map();
  if (!Array.isArray(allMistakes)) return m;
  for (const x of allMistakes) {
    if (!x) continue;
    const label = x.name || x.label || x.id;
    if (x.id)   m.set(x.id, label);
    if (x.name) m.set(x.name, label);
  }
  return m;
};

const labelFor = (key, labelMap) => labelMap.get(key) || key;

const mistakesOf = (trade) => {
  const arr = Array.isArray(trade?.mistakes) ? trade.mistakes : [];
  return arr.filter(Boolean);
};

const isWin  = (t) => Number.isFinite(t?.netPnl) && t.netPnl > 0;
const isLoss = (t) => Number.isFinite(t?.netPnl) && t.netPnl <= 0;

/**
 * Parse "HH:MM" ET to an integer hour (0-23). Used for time-of-day bucketing.
 * Returns null on unparseable input — those trades are dropped from the bucket
 * analysis but still count toward setup-wide stats.
 */
const hourOf = (entryTime) => {
  if (!entryTime || typeof entryTime !== 'string') return null;
  const m = entryTime.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  return Number.isFinite(h) && h >= 0 && h <= 23 ? h : null;
};

// ── Pattern 1: Setup mistake-rate spike ─────────────────────────────────
//
// Compute mistake-per-trade ratio for this setup vs the trader's overall.
// "Mistakes per trade" double-counts trades with multiple mistakes — that's
// the right metric here because two mistakes on one trade is genuinely
// twice the discipline gap of one.

const detectSetupRateSpike = (setupName, setupTrades, allTrades) => {
  if (setupTrades.length < SETUP_RATE_MIN_TRADES) return null;
  if (!Array.isArray(allTrades)) return null;
  const otherTrades = allTrades.filter(t => t && t.setup !== setupName);
  if (otherTrades.length < BASELINE_MIN_TRADES) return null;

  const mistakeCount = (trades) =>
    trades.reduce((sum, t) => sum + mistakesOf(t).length, 0);
  const setupRate = mistakeCount(setupTrades) / setupTrades.length;
  const baselineRate = mistakeCount(otherTrades) / otherTrades.length;
  const gap = setupRate - baselineRate;
  if (gap < SETUP_RATE_GAP) return null;

  return makeObservation({
    id: `${key}:${setupName}:rate-spike`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `"${setupName}" attracts ${(setupRate / Math.max(baselineRate, 0.01)).toFixed(1)}× the mistakes of your other setups`,
    detail: `${setupRate.toFixed(2)} mistakes per trade on this setup (${setupTrades.length} trades). Your other-setups baseline: ${baselineRate.toFixed(2)} per trade (${otherTrades.length} trades). The setup itself is structurally inviting the error — review the rules or environment around it, not just the trades.`,
    action: `Look at WHY this setup is mistake-prone. Often it's signal ambiguity (overlap with another setup), or an entry trigger that's easy to confuse with FOMO/chop.`,
    evidence: {
      setupRate, baselineRate, gap,
      setupTradeCount: setupTrades.length,
      baselineTradeCount: otherTrades.length,
    },
    confidence: 0.78,
    priority: 78,
  });
};

// ── Pattern 2: Time-of-day mistake clustering ───────────────────────────
//
// Within this setup's history, find HOUR buckets where mistakes-per-trade is
// meaningfully higher than the setup average. Names the window the trader
// should be most-cautious in.

const detectHourClustering = (setupName, setupTrades) => {
  if (setupTrades.length < SETUP_RATE_MIN_TRADES) return null;
  const buckets = new Map(); // hour → { trades, mistakes }
  let totalMistakes = 0;
  for (const t of setupTrades) {
    const h = hourOf(t.entryTime);
    if (h == null) continue;
    if (!buckets.has(h)) buckets.set(h, { trades: 0, mistakes: 0 });
    const b = buckets.get(h);
    b.trades++;
    const m = mistakesOf(t).length;
    b.mistakes += m;
    totalMistakes += m;
  }
  if (totalMistakes === 0 || buckets.size === 0) return null;
  const setupAvgRate = totalMistakes / setupTrades.length;
  if (setupAvgRate <= 0) return null;

  // Find the worst bucket: highest rate, must have minimum sample + lift.
  let worst = null;
  for (const [hour, b] of buckets) {
    if (b.trades < HOUR_BUCKET_MIN_TRADES) continue;
    const rate = b.mistakes / b.trades;
    const lift = rate / setupAvgRate;
    if (lift < HOUR_BUCKET_MIN_LIFT) continue;
    if (!worst || rate > worst.rate) worst = { hour, ...b, rate, lift };
  }
  if (!worst) return null;

  const fmtHour = (h) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const display = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return `${display}:00 ${ampm}`;
  };
  const window = `${fmtHour(worst.hour)}-${fmtHour((worst.hour + 1) % 24)} ET`;

  return makeObservation({
    id: `${key}:${setupName}:hour-cluster:${worst.hour}`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `"${setupName}" mistakes spike in the ${window} window`,
    detail: `${worst.mistakes} mistakes across ${worst.trades} trades in that hour (${worst.rate.toFixed(2)} per trade) — ${worst.lift.toFixed(1)}× the setup's average rate of ${setupAvgRate.toFixed(2)}.`,
    action: `Cap or skip this setup during ${window}. The pattern is repeating; you don't need more data, you need a rule.`,
    evidence: {
      hour: worst.hour,
      bucketTrades: worst.trades,
      bucketMistakes: worst.mistakes,
      bucketRate: worst.rate,
      setupAvgRate,
      lift: worst.lift,
    },
    confidence: 0.75,
    priority: 76,
  });
};

// ── Pattern 3: Loss-cause attribution ───────────────────────────────────
//
// Among this setup's losers that have at least one mistake tag, which mistake
// recurs most often? Names the cause when it's overwhelming (≥50% share).

const detectLossCause = (setupName, setupTrades, labelMap) => {
  const losersWithMistakes = setupTrades.filter(t => isLoss(t) && mistakesOf(t).length > 0);
  if (losersWithMistakes.length < LOSS_CAUSE_MIN_LOSERS) return null;

  const freq = new Map();
  for (const t of losersWithMistakes) {
    for (const m of mistakesOf(t)) freq.set(m, (freq.get(m) || 0) + 1);
  }
  if (freq.size === 0) return null;

  const sorted = [...freq.entries()].sort((a, b) => b[1] - a[1]);
  const [topKey, topCount] = sorted[0];
  const share = topCount / losersWithMistakes.length;
  if (share < LOSS_CAUSE_MIN_SHARE) return null;

  const topLabel = labelFor(topKey, labelMap);
  // Rough net bleed from these losers — the dollar cost of the cause.
  const bleed = losersWithMistakes
    .filter(t => mistakesOf(t).includes(topKey))
    .reduce((s, t) => s + (Number.isFinite(t.netPnl) ? t.netPnl : 0), 0);

  // JUMP_TO_TRADE on the WORST loser tagged with this mistake — concrete
  // example beats a stat.
  const tagged = losersWithMistakes.filter(t => mistakesOf(t).includes(topKey));
  let worst = null; let worstPnl = Infinity;
  for (const t of tagged) {
    if (Number.isFinite(t.netPnl) && t.netPnl < worstPnl) { worstPnl = t.netPnl; worst = t; }
  }
  const jumpAction = worst?.id
    ? [makeAction({
        type: ACTION_TYPE.JUMP_TO_TRADE,
        label: 'Show worst case',
        payload: { tradeId: worst.id, date: worst.date || null },
        doneLabel: 'Jumping…',
      })]
    : null;

  return makeObservation({
    id: `${key}:${setupName}:loss-cause:${topKey}`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `"${setupName}" loses to ${topLabel} — tagged on ${topCount}/${losersWithMistakes.length} of mistake-tagged losers (${fmtPct(share)})`,
    detail: `Net bleed from ${topLabel}-tagged losers on this setup: $${Math.round(bleed).toLocaleString()}. The same cause keeps showing up — the fix is upstream of the setup, in the mental state or environment that triggers it.`,
    action: `Build a pre-trade checklist that filters for ${topLabel}. If you can't catch it before the entry, move the rule earlier — into the watchlist build or the morning prep.`,
    actions: jumpAction,
    evidence: {
      mistakeKey: topKey,
      mistakeLabel: topLabel,
      count: topCount,
      sampleSize: losersWithMistakes.length,
      share,
      bleed,
      worstTradeId: worst?.id ?? null,
    },
    confidence: 0.8,
    priority: 82,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { setupName, setupTrades, allTrades, allMistakes = [] } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  // Live-trade gate: outcome-based analysis must run on closed trades only.
  // Loss-cause attribution, mistake-rate spike vs baseline, and hour-bucket
  // win/loss math all depend on realized P&L; open trades' unrealized P&L
  // isn't a realized outcome and including them fires premature "this setup
  // loses to X" claims while the user is still in the trade. Filter both
  // the primary cohort AND the baseline. Matches summarizePnl semantics.
  const closedSetupTrades = setupTrades.filter(t => t?.isOpen !== true);
  if (closedSetupTrades.length === 0) return null;
  const closedAllTrades = (allTrades || []).filter(t => t?.isOpen !== true);

  const labelMap = buildLabelMap(allMistakes);
  const out = [];

  const rateSpike = detectSetupRateSpike(setupName, closedSetupTrades, closedAllTrades);
  if (rateSpike) out.push(rateSpike);

  const hourCluster = detectHourClustering(setupName, closedSetupTrades);
  if (hourCluster) out.push(hourCluster);

  const lossCause = detectLossCause(setupName, closedSetupTrades, labelMap);
  if (lossCause) out.push(lossCause);

  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  buildLabelMap,
  mistakesOf,
  hourOf,
  detectSetupRateSpike,
  detectHourClustering,
  detectLossCause,
  SETUP_RATE_MIN_TRADES,
  BASELINE_MIN_TRADES,
  SETUP_RATE_GAP,
  HOUR_BUCKET_MIN_TRADES,
  HOUR_BUCKET_MIN_LIFT,
  LOSS_CAUSE_MIN_LOSERS,
  LOSS_CAUSE_MIN_SHARE,
};
